import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { Fragment, useEffect, useCallback, useState, useMemo, useContext } from "react";
import { Auth } from 'aws-amplify';

import { toast } from 'react-toastify';
import Tab, { TabProps } from "../../../molecules/Tab";
import PageForm from "../../../templates/PageForm";
import { computed, useSignal } from "@preact/signals-react";
import { ErrorField } from "../../../../types/ErrorField";
import useFetch from "../../../../hooks/useFetch";
import { TbUser, TbShield, TbHeart, TbPencil, TbCreditCard, TbMapPin, TbPhone, TbFileText } from "react-icons/tb";
import { storage } from "../../../../utils/storage";
import { removeErrorField } from "../../../../utils/formErrorUtils";
import IndividualFormBasicData from "./IndividualFormBasicData";
import IndividualFormAdditionalData from "./IndividualFormAdditionalData";
import IndividualFormSubscription from "./IndividualFormSubscription";
import IndividualFormConsentingParty from "./IndividualFormConsentingParty";
import PersonFormBankAccount from "../shared/PersonFormBankAccount";
import PersonFormAddresses from "../shared/PersonFormAddresses";
import PersonFormContacts from "../shared/PersonFormContacts";
import PersonFormDocuments from "../shared/PersonFormDocuments";
import useCities from "../../../../hooks/useCities";
import useProvinces from "../../../../hooks/useProvinces";
import { useProfileLoader } from "../../../../hooks/useProfileLoader";
import { ProfileStorageManager } from "../../../../utils/profileStorageManager";
import appConfig from "../../../../config/app-config";
import { AuthContext } from "../../../../services/Auth/AuthContext";
import { canEditPerson } from "../../../../services/Auth/roles";

const { address: PERSON_API_URL, key: PERSON_API_KEY } = appConfig.apis.personApiEnv;
const { address: GEOLOCATION_API_URL, key: GEOLOCATION_API_KEY } = appConfig.apis.geolocationApiEnv;

const BASE_URL = `${PERSON_API_URL}/individual`;
const API_KEY = PERSON_API_KEY;

const profileTabList = [
    {
        name: 'CONSENTING_PARTY-ISSUER-GUARANTOR',
        id: 'tab-1',
        title: 'Dados complementares',
        index: 1,
        icon: TbUser,  // 👤 Dados pessoais
    },
    {
        name: 'CONSENTING_PARTY-GUARANTOR-ISSUER-SIGNATORY',
        id: 'tab-2',
        title: 'Assinatura',
        index: 2,
        icon: TbPencil,  // ✏️ Assinatura
    },
    {
        name: 'GUARANTOR-ISSUER-SIGNATORY',
        id: 'tab-3',
        title: 'Anuentes',
        index: 3,
        icon: TbShield,  // 🛡️ Avalista/Garantidor
    },
    {
        name: 'ALL',
        id: 'tab-4',
        title: 'Endereços',
        index: 4,
        icon: TbMapPin,  // 📍 Endereços
    },
    {
        name: 'ALL',
        id: 'tab-5',
        title: 'Contatos',
        index: 5,
        icon: TbPhone,  // 📞 Contatos
    },
    {
        name: 'ALL',
        id: 'tab-6',
        title: 'Documentos',
        index: 6,
        icon: TbFileText,  // 📄 Documentos
    },
    {
        name: 'FINANCIAL_REPRESENTATIVE',
        id: 'tab-7',
        title: 'Contas bancárias',
        index: 7,
        icon: TbCreditCard,  // 💳 Financeiro
    }
]

type Address = {
    person_id: string;
    id: string;
    zipcode: string;
    street: string;
    street_number: string;
    complement: string;
    district: string;
    city_id: string;
    street_type: string;
    is_active: boolean;
    province_initials: string;
}

type Form = {
    id: string;
    uuid: string;
    socialId: string;
    name: string;
    birthday: string;
    maritalStatus: string;
    nationalityId: string;
    professionId: string;
    professionName?: string;
    matrimonialRegimeId: string;
    spouseSocialId: string;
    spouseName?: string;
    gender: string;
    isCountryman: string;
    PEP: string;
    activeAddress: Address;
    idCardType: string;
    idCard: string;
    idCardProvince: string;
    issuingAuthority: string;
    email: string;
    phone: string;
    mobile: string;
    isNationalNumber: string;
    addresses: any[];
    profiles: any[];
    contacts: any[];
    selectedSubscriptionType: any[];
    province: string;
    banks: any[];
    bankAccounts: any[];
    consentingParty: {
        uuid: string;
        name: string;
        socialId: string;
    }
    selectedConsentingParty?: string;
    blockEdit: {
        blockIdCard: boolean;
        blockBirthday: boolean;
        blockNationality: boolean;
    }
}

type DataRow = {
    idx: number;
    profile: string;
    label: string;
    signAs: string;
    useDigitalCertificate: boolean;
    canDelete: boolean;
}

const IndividualPageEdit = () => {
    const { fetchCities } = useCities();
    const { fetchProvinces } = useProvinces();
    const banks = useSignal<any[]>([]);
    const { personId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const isSendingForm = useSignal<boolean>(false);
    const errorFields = useSignal<ErrorField[]>([]);
    const apiVersion = useSignal('');
    const isLoadingForm = useSignal<boolean>(false);
    const provinces = useSignal<any[]>([]);
    const cities = useSignal<any[]>([]);
    const genders = useSignal<any[]>([
        { value: '0', label: 'Feminino' },
        { value: '1', label: 'Masculino' }
    ]);
    const subscriptionTypes = useSignal<any[]>([]);
    const individualUUID = useSignal<string>('');
    const isEditing = useSignal<boolean>(false);
    // ✅ Buscar profiles da API ao invés de hardcoded
    const [profilesMap, setProfilesMap] = useState<{ [key: string]: string }>({});
    const [dbProfiles, setDbProfiles] = useState<string[]>([]); // ✅ Profiles do banco

    useEffect(() => {
        const loadProfilesMap = async () => {
            try {
                const token = localStorage.getItem('authToken');
                const response = await fetch(`${PERSON_API_URL}/profiles/individual`, {
                    headers: {
                        'x-api-key': API_KEY,
                        'Authorization': `Bearer ${token}`,
                    }
                });
                if (response.ok) {
                    const data = await response.json();
                    const map: { [key: string]: string } = {};
                    data.profiles?.forEach((p: any) => {
                        map[p.value] = p.label;
                    });
                    setProfilesMap(map);
                }
            } catch (error) {
                console.error('Erro ao carregar profiles map:', error);
            }
        };
        loadProfilesMap();
    }, []);

    const form = useSignal<Form>({
        id: '',
        uuid: '',
        socialId: '',
        name: '',
        birthday: '',
        maritalStatus: '',
        nationalityId: '',
        professionId: '',
        professionName: '',
        matrimonialRegimeId: '',
        spouseSocialId: '',
        spouseName: '',
        gender: '',
        isCountryman: '',
        PEP: '',
        province: '',
        email: '',
        phone: '',
        mobile: '',
        isNationalNumber: "true",
        idCardType: '',
        idCard: '',
        idCardProvince: '',
        issuingAuthority: '',
        activeAddress: {
            person_id: '',
            id: '',
            zipcode: '',
            street: '',
            street_number: '',
            complement: '',
            district: '',
            city_id: '',
            street_type: '',
            is_active: false,
            province_initials: '',
        },
        addresses: [],
        profiles: [],
        contacts: [],
        selectedSubscriptionType: [],
        banks: [],
        bankAccounts: [],
        consentingParty: {
            uuid: '',
            name: '',
            socialId: '',
        },
        selectedConsentingParty: '',
        blockEdit: {
            blockIdCard: false,
            blockBirthday: false,
            blockNationality: false,
        }
    });


    const tabItemsList: TabProps[] = [
        { id: "tab-0", title: 'Dados básicos', index: 0, icon: TbUser },
        { id: "tab-1", title: 'Dados Complementares', index: 1, icon: TbPencil },
        { id: "tab-2", title: 'Assinaturas', index: 2, icon: TbShield },
        { id: "tab-3", title: 'Anuentes', index: 3, icon: TbHeart },
        { id: "tab-4", title: 'Endereços', index: 4, icon: TbMapPin },
        { id: "tab-5", title: 'Contatos', index: 5, icon: TbPhone },
        { id: "tab-6", title: 'Documentos', index: 6, icon: TbFileText },
        { id: "tab-7", title: 'Contas bancárias', index: 7, icon: TbCreditCard },
    ];
    const tabItems = useSignal<TabProps[]>([]);
    const [searchParams] = useSearchParams();
    const tabIndex: number = parseInt(searchParams.get('tab') || "0");
    const profiles = searchParams.get('profiles') || '';  // ✅ Manter para compatibilidade
    const user = useContext(AuthContext)?.user ?? null;
    const readOnly = !canEditPerson(user);

    // ✅ Estado para forçar recarregamento dos dados
    const [refreshKey, setRefreshKey] = useState(0);

    const { isLoading, data, error, isTokenError } = useFetch(`${BASE_URL}/${personId}/page-form?_refresh=${refreshKey}`, [{ key: 'x-api-key', value: API_KEY }]);

    // ✅ Função para recarregar dados após mudanças em contas bancárias
    const handleBankAccountChange = () => {
        setRefreshKey(prev => prev + 1);
    };

    // ✅ Extrair profiles do banco quando data carregar
    useEffect(() => {
        if (data?.profiles) {
            const profiles = data.profiles.map((p: any) => p.type);
            setDbProfiles(profiles);
        }
    }, [data]);

    // 🔧 CORREÇÃO: SEMPRE usar personId (UUID estável) para localStorage
    const getPersonUUID = useCallback(() => {
        return personId; // Chave estável, sempre UUID correto
    }, [personId]);

    // ✅ CORREÇÃO: Memoizar a função loadFromDatabase para evitar loop infinito
    const loadFromDatabase = useCallback(async () => {
        try {
            const token = await Auth.currentSession()
                .then((user) => user.getIdToken().getJwtToken());

            const response = await fetch(`${BASE_URL}/${personId}`, {
                headers: {
                    'x-api-key': API_KEY,
                    'Authorization': `Bearer ${token}`,
                }
            });
            if (response.ok) {
                const data = await response.json();
                const profiles = data.profiles?.map((p: any) => p.type) || [];
                setDbProfiles(profiles); // ✅ Salvar profiles do banco
                return profiles;
            }
        } catch (error) {
            console.warn('Erro ao carregar profiles do banco:', error);
        }
        return [];
    }, [personId]); // ✅ Dependência estável

    // ✅ CORREÇÃO: SEMPRE usar personId (UUID) - NUNCA individualUUID.value
    const personUUID = personId; // Chave estável, não muda

    // Hooks devem vir ANTES de qualquer return condicional
    const { profiles: loadedProfiles, saveProfiles } = useProfileLoader(
        'individual',
        personUUID || '',
        loadFromDatabase // ✅ Função memoizada, não recriada a cada render
    );

    const profilesArray = useSignal<string[]>(loadedProfiles || []);
    const profilesSubscription = useSignal<DataRow[]>([]);

    const getTabItem = (id: string): TabProps => {
        const tab = tabItemsList.find(item => item.id === id);
        if (!tab) {
            throw new Error(`Tab with id "${id}" not found`);
        }
        return tab;
    }

    const addTab = (tab: TabProps) => {
        if (!tabItems.value.find(item => item.id === tab.id)) {
            tabItems.value.push(tab);
        }
        tabItems.value.sort((a, b) => a.index - b.index);
    }

    const loadDefaultSubscriptionTypes = (file: any) => {
        // ✅ CORREÇÃO: Verificação de segurança para evitar erro null
        if (!file) {
            console.warn('⚠️ loadDefaultSubscriptionTypes: file é null, pulando execução');
            return;
        }

        let values: DataRow[] = [];
        //carrega os perfis de assinatura que ja estao cadastrados
        //este codigo e para carregar os tipos de asinatura que estao na base de dados, o codigo seguinte preenche os tipos de asinatura a partir dos perfis selecionados
        if (file.profiles && file.profiles.length > 0) {
            file.profiles.forEach((profile: any) => {
                let sign_as = profile.sign_as;
                if (sign_as && sign_as.length > 0) {
                    sign_as.forEach((record: any) => {
                        if (!profileSubscriptionContains(values, record.sign_as)) {
                            let item = {
                                idx: record.id,
                                profile: profile.type,
                                label: profilesMap[profile.type] || '',
                                useDigitalCertificate: record.use_digital_certificate || false,
                                canDelete: true,
                                signAs: record.sign_as || ''
                            };
                            values.push(item);
                        }
                    });
                }
            });
        }

        let tmp = ["CONSENTING_PARTY"]
        if (profilesArray.value.some((profile: string) => tmp.includes(profile))) {
            let record = subscriptionTypes.value.find((s: any) => s.profile === "CONSENTING_PARTY");
            if (!profileSubscriptionContains(values, record.sign_as)) {
                let row = {
                    profile: record.profile,
                    label: record.labelProfile,
                    useDigitalCertificate: record.useDigitalCertificate || false,
                    idx: record.value,
                    canDelete: false,
                    signAs: record.sign_as || ''
                }
                values.push(row);
            }
        }
        tmp = ["GUARANTOR"]
        if (profilesArray.value.some((profile: string) => tmp.includes(profile))) {
            let record = subscriptionTypes.value.find((s: any) => s.profile === "GUARANTOR");
            if (!profileSubscriptionContains(values, record.sign_as)) {
                let row = {
                    profile: record.profile,
                    label: record.labelProfile,
                    useDigitalCertificate: record.useDigitalCertificate || false,
                    idx: record.value,
                    canDelete: false,
                    signAs: record.sign_as || ''
                }
                values.push(row);
            }
        }
        tmp = ["ISSUER"]
        if (profilesArray.value.some((profile: string) => tmp.includes(profile))) {
            let record = subscriptionTypes.value.find((s: any) => s.profile === "ISSUER");
            if (!profileSubscriptionContains(values, record.sign_as)) {
                let row = {
                    profile: record.profile,
                    label: record.labelProfile,
                    useDigitalCertificate: record.useDigitalCertificate || false,
                    idx: record.value,
                    canDelete: false,
                    signAs: record.sign_as || ''
                }
                values.push(row);
            }
        }


        form.value = {
            ...form.value,
            selectedSubscriptionType: values,
        }
    }

    const loadProfilesSubscription = async () => {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${BASE_URL}/subscription-profiles`, {
            method: 'GET',
            headers: {
                'x-api-key': API_KEY,
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            }
        });

        if (!response.ok) {
            toast.error("Erro ao carregar os perfis de assinatura.");
            return;
        }
        const responseData = await response.json();
        let profilesSubscription = responseData.Profiles || [];
        let idx = 0;
        let itens: any[] = [];
        let profiles = profilesArray.value;

        profilesSubscription.forEach((element: any) => {
            if (profiles.includes(element.profile)) {
                element.values.forEach((value: any) => {
                    let item = {
                        profile: element.profile,
                        label: element.label + "-" + value.sign_as,
                        labelProfile: element.label,
                        sign_as: value.sign_as,
                        value: idx++,
                    }
                    itens.push(item);
                });
            }
        });
        subscriptionTypes.value = itens;

        // ✅ CORREÇÃO: Verificar se data existe antes de chamar loadDefaultSubscriptionTypes
        if (data && data.profiles) {
            loadDefaultSubscriptionTypes(data);
        }
    }

    const validarCPF = (socialId: string) => {
        // Remove caracteres não numéricos
        const cpfLimpo = socialId.replace(/[^\d]/g, '');
        // Verifica se o CPF tem 11 dígitos e se não são todos iguais (ex: "111.111.111-11")
        if (cpfLimpo.length !== 11 || /^(\d)\1{10}$/.test(cpfLimpo)) {
            return false;
        }
        let soma: number;
        let resto: number;
        // Validação do primeiro dígito verificador
        soma = 0;
        for (let i = 0; i < 9; i++) {
            soma += parseInt(cpfLimpo.charAt(i)) * (10 - i);
        }
        resto = (soma * 10) % 11;
        if (resto === 10 || resto === 11) {
            resto = 0;
        }
        if (resto !== parseInt(cpfLimpo.charAt(9))) {
            return false;
        }
        // Validação do segundo dígito verificador
        soma = 0;
        for (let i = 0; i < 10; i++) {
            soma += parseInt(cpfLimpo.charAt(i)) * (11 - i);
        }
        resto = (soma * 10) % 11;
        if (resto === 10 || resto === 11) {
            resto = 0;
        }
        if (resto !== parseInt(cpfLimpo.charAt(10))) {
            return false;
        }
        return true;
    }


    useEffect(() => {
        formatMobilePhone();
    }, [form.value.isNationalNumber])

    // ✅ Sincronizar profiles carregados com profilesArray E forçar reload
    useEffect(() => {
        if (loadedProfiles.length > 0 && JSON.stringify(loadedProfiles) !== JSON.stringify(profilesArray.value)) {
            profilesArray.value = loadedProfiles;
            alertTrigger.value++;

            // ✅ FORÇAR atualização das abas quando profiles mudam
            profilesArray.value.forEach((profile: any) => {
                profileTabList.forEach(profileTab => {
                    if (profileTab.name.includes(profile)) {
                        const tabItem = profileTab;
                        addTab(getTabItem(tabItem.id));
                    }
                });
            });
        }
    }, [loadedProfiles]);

    // ✅ CORREÇÃO: Evitar conflito entre loadedProfiles e individualUUID
    useEffect(() => {
        // Só executar se NÃO há profiles já carregados via loadedProfiles
        if (individualUUID.value &&
            individualUUID.value !== 'temp-individual-id' &&
            loadedProfiles.length === 0) { // ← CONDIÇÃO CRÍTICA
        }
    }, [individualUUID.value, loadedProfiles.length]); // ← Dependência dupla

    const loadProvinces = async () => {
        isLoadingForm.value = true;
        try {
            provinces.value = await fetchProvinces();
        } catch (error) {
            toast.error("Erro ao carregar os estados.");
        }
        isLoadingForm.value = false;
    };

    const fetchCitiesWrapper = async (province: string) => {
        try {
            isLoadingForm.value = true;
            cities.value = await fetchCities(province);
        } catch (error) {
            toast.error("Erro ao carregar as cidades. Tente novamente!");
        } finally {
            isLoadingForm.value = false;
        }
    };

    const alertTrigger = useSignal(0);

    const [missingFields, setMissingFields] = useState<string[]>([]);

    useEffect(() => {
        const missing: string[] = [];
        const prf = profilesArray.value;

        // Perfis que exigem endereço completo (todos exceto DRAWEE)
        const addressRequiredProfiles = ['SIGNATORY', 'GUARANTOR', 'ISSUER', 'CONSENTING_PARTY', 'RECIPIENT'];
        const needsAddress = prf.some((p: string) => addressRequiredProfiles.includes(p));

        if (needsAddress) {
            const addr = form.value.activeAddress;
            if (!addr?.zipcode || addr.zipcode.replace(/\D/g, '') === '') missing.push("CEP");
            if (!addr?.street_type) missing.push("Tipo de logradouro");
            if (!addr?.street) missing.push("Logradouro");
            if (!addr?.street_number) missing.push("Número");
            if (!addr?.district) missing.push("Bairro");
            if (!addr?.province_initials) missing.push("Estado");
            if (!addr?.city_id) missing.push("Cidade");
        }

        if (prf.includes("ISSUER")) {
            if (!form.value.birthday) missing.push("Data de nascimento");
            if (!form.value.nationalityId) missing.push("Nacionalidade");
            if (!form.value.maritalStatus) missing.push("Estado civil");
        }

        if (prf.includes("GUARANTOR")) {
            if (!form.value.birthday) missing.push("Data de nascimento");
            if (!form.value.nationalityId) missing.push("Nacionalidade");
            if (!form.value.professionId) missing.push("Profissão");
            if (!form.value.maritalStatus) missing.push("Estado civil");
            if (form.value.maritalStatus === '2' || form.value.maritalStatus === '6') {
                if (!form.value.matrimonialRegimeId) missing.push("Regime de casamento");
                if (form.value.matrimonialRegimeId === "1" || form.value.matrimonialRegimeId === "2") {
                    if (!form.value.spouseSocialId) missing.push("CPF do cônjuge");
                }
            }
        }

        if (prf.includes("CONSENTING_PARTY")) {
            if (!form.value.birthday) missing.push("Data de nascimento");
        }

        if (prf.includes("RECIPIENT")) {
            if (!form.value.birthday) missing.push("Data de nascimento");
            if (!form.value.nationalityId) missing.push("Nacionalidade");
        }

        setMissingFields(Array.from(new Set(missing)));
    }, [profilesArray.value, form.value, alertTrigger.value]);

    const updateButtonDisable = computed(() => {
        return missingFields.length > 0;
    });

    const getCity = async (cityId: number) => {
        isLoadingForm.value = true;
        try {
            const response = await fetch(`${GEOLOCATION_API_URL}/cities/${cityId}?fields=id,name,province.initials`, {
                headers: {
                    "x-api-key": GEOLOCATION_API_KEY,
                },
            });
            isLoadingForm.value = false;
            if (response.ok) {
                return await response.json();
            }
            console.warn('Erro ao carregar cidade:', response.status);
            return null;
        } catch (error) {
            console.warn('Erro CORS ao carregar cidade:', error);
            // Não mostrar toast para não poluir UI
            return null;
        } finally {
            isLoadingForm.value = false;
        }
    };

    const cancelForm = () => {
        // 🧹 CORREÇÃO: Limpar localStorage ao cancelar
        const personUUID = getPersonUUID();
        if (!personUUID) return;
        ProfileStorageManager.clear('individual', personUUID);

        // Usar estado persistido ou fallback para padrão DESC
        const listState = storage.getIndividualListState();
        const filters = storage.getIndividualFilters();
        const { page, pageSize, field, order } = listState;
        const { txtNameSocialId, txtEmail, txtMobile } = filters;

        const url = `/individual/individual-list/${page}/${pageSize}/${field}/${order}/p=${txtNameSocialId};r=${txtMobile};s=${txtEmail};`;
        navigate(url);
    };

    const closeForm = () => {
        window.close();
        if (window.history.length > 1) {
            navigate(-1);
            return;
        }
        navigate('/person/signatories');
    };

    const validateRequiredFields = (): string[] => {
        const missing: string[] = [];
        if (!form.value.socialId?.replace(/\D/g, '')) missing.push('CPF');
        if (!form.value.name?.trim()) missing.push('Nome');
        if (!form.value.mobile?.replace(/\D/g, '')) missing.push('Telefone Celular');
        if (!form.value.email?.trim()) missing.push('Email');
        // Campos condicionais por profile
        missing.push(...missingFields);
        return Array.from(new Set(missing));
    };

    const submitForm = async () => {
        const missing = validateRequiredFields();
        if (missing.length > 0) {
            toast.error(`Campos obrigatórios não preenchidos: ${missing.join(', ')}`, { autoClose: 8000 });
            return false;
        }

        if (form.value.spouseSocialId !== "" && !validarCPF(form.value.spouseSocialId)) {
            toast.error("CPF do cônjuge inválido. Por favor, verifique o CPF informado.");
            return false;
        }
        if (!verifySelectedSubscriptionType()) {
            return false;
        } else {
            isSendingForm.value = true;
            try {
                form.value.contacts.forEach((contact: any) => {
                    if (contact.type === 'MOBILE') {
                        contact.is_national_number = form.value.isNationalNumber === "true" ? true : false;
                        contact.content = form.value.mobile.replace(/\D/g, '');
                    }
                    if (contact.type === 'PHONE') {
                        contact.content = form.value.phone.replace(/\D/g, '');
                    }
                    if (contact.type === 'EMAIL') {
                        contact.content = form.value.email;
                    }
                });

                // ✅ SIMPLIFICADO: usar person.uuid como fonte da verdade
                let finalProfiles = profilesArray.value;

                // Garantir que localStorage seja a fonte da verdade
                try {
                    const personUUID = getPersonUUID();
                    if (!personUUID) throw new Error('PersonUUID not found');
                    const storedProfiles = ProfileStorageManager.load('individual', personUUID);
                    if (storedProfiles.length > 0) {
                        finalProfiles = storedProfiles;
                    }
                } catch (error) {
                    console.warn('⚠️ Erro ao ler localStorage no update, usando profilesArray:', error);
                }

                form.value = {
                    ...form.value,
                    profiles: finalProfiles,
                }
                var dataToSubmit = form.value;
                dataToSubmit.mobile = dataToSubmit.mobile.replace(/\D/g, '');
                dataToSubmit.phone = dataToSubmit.phone.replace(/\D/g, '');

                // ✅ Normalizar consentingParty para sempre ser objeto
                if (dataToSubmit.selectedConsentingParty && dataToSubmit.selectedConsentingParty !== '') {
                    // Se selectedConsentingParty existe, usar como UUID
                    dataToSubmit.consentingParty = {
                        uuid: dataToSubmit.selectedConsentingParty,
                        name: dataToSubmit.consentingParty?.name || '',
                        socialId: dataToSubmit.consentingParty?.socialId || ''
                    };
                } else if (typeof dataToSubmit.consentingParty === 'string' && dataToSubmit.consentingParty !== '') {
                    // Se consentingParty é string UUID, converter para objeto
                    dataToSubmit.consentingParty = {
                        uuid: dataToSubmit.consentingParty,
                        name: '',
                        socialId: ''
                    };
                } else if (!dataToSubmit.consentingParty || dataToSubmit.consentingParty.uuid === '') {
                    // Se não há anuente, enviar objeto vazio
                    dataToSubmit.consentingParty = {
                        uuid: '',
                        name: '',
                        socialId: ''
                    };
                }

                // Não enviar activeAddress se zipcode estiver vazio
                if (!dataToSubmit.activeAddress?.zipcode || dataToSubmit.activeAddress.zipcode.trim() === '') {
                    const { activeAddress, ...cleanData } = dataToSubmit;
                    dataToSubmit = cleanData as typeof dataToSubmit;
                }

                if (dataToSubmit.idCardType === "RNE") {
                    dataToSubmit.idCardProvince = "";
                    dataToSubmit.issuingAuthority = "";
                }

                const token = localStorage.getItem('authToken');
                const response = await fetch(`${BASE_URL}` + "/edit", {
                    method: 'POST',
                    headers: {
                        'x-api-key': API_KEY,
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify(dataToSubmit),
                });
                if (response.status === 401) {
                    navigate("/denied");
                    return;
                }
                if (response.ok) {
                    // ✅ Limpar localStorage após sucesso
                    const personUUID = getPersonUUID();
                    if (personUUID) {
                        ProfileStorageManager.clear('individual', personUUID);
                    }

                    toast.success('Dados gravados com sucesso!');
                    const listState = storage.getIndividualListState();
                    const filters = storage.getIndividualFilters();
                    const { page, pageSize, field, order } = listState;
                    const { txtNameSocialId, txtEmail, txtMobile } = filters;
                    navigate(`/individual/individual-list/${page}/${pageSize}/${field}/${order}/p=${txtNameSocialId};r=${txtMobile};s=${txtEmail};`);
                } else {
                    try {
                        const responseData = await response.json();
                        const errors = responseData.errors || [];
                        if (errors.length > 0) {
                            errorFields.value = errors.map((error: any) => ({ fieldName: error.field, message: error.message }));
                            errorFields.value.forEach((errorField: ErrorField) => {
                                toast.error(errorField.message);
                            });
                        } else {
                            const message = responseData.message || responseData.error || `Erro ao salvar (${response.status})`;
                            toast.error(message, { autoClose: 8000 });
                        }
                    } catch (parseError) {
                        console.error('Erro ao processar resposta do servidor:', parseError);
                        toast.error(`Erro ao salvar os dados (${response.status}). Verifique os campos obrigatórios.`, { autoClose: 8000 });
                    }
                }
            } catch (error) {
                console.error('Erro de comunicação com o servidor:', error);
                toast.error("Ocorreu um erro na comunicação com o servidor!\nTente novamente mais tarde.");
            }
            isSendingForm.value = false;
        }
    };

    const verifySelectedSubscriptionType = () => {
        // de acordo com a especificação, é necessário ter pelo menos um tipo de assinatura para cada tipo de perfil selecionado
        let selectedProfiles = profilesArray.value;
        let selectedSubscriptionTypes = form.value.selectedSubscriptionType;
        let count_not_found = 0;
        selectedProfiles.forEach((profile: string) => {
            var found = false;
            selectedSubscriptionTypes.forEach((subscription: DataRow) => {
                if ((subscription.profile === profile)) {
                    found = true;
                }
            });
            if (!found && (profile !== 'FINANCIAL_REPRESENTATIVE') && (profile !== 'DRAWEE')) {
                toast.error(`É necessário selecionar pelo menos um tipo de assinatura para o perfil ${profilesMap[profile] || profile}.`);
                count_not_found++;
            }
        });
        return count_not_found === 0;
    }

    if (isTokenError) {
        navigate("/denied");
    }

    if (error) {
        navigate("/network-error");
    }

    const formatMobilePhone = () => {
        let phone = form.value.mobile;
        phone = phone.replace(/\D/g, '')
        if (form.value.isNationalNumber === "true") {
            phone = phone.replace(/\D/g, '').replace(/(\d{2})(\d)/, '($1)$2').replace(/(\d{5})(\d)/, '$1-$2').replace(/(-\d{4})\d+?$/, '$1');
        }
        form.value = {
            ...form.value,
            mobile: phone,
        }
    }

    useEffect(() => {
        if (data && personId) {
            // 🧹 CORREÇÃO: Verificar se é acesso direto à edição
            const personUUID = getPersonUUID();
            if (!personUUID) return;
            const storedProfiles = ProfileStorageManager.load('individual', personUUID);

            // Se há profiles no localStorage mas é acesso direto (não veio do modal)
            if (storedProfiles && storedProfiles.length > 0 &&
                (location.state?.fromDirectEdit || !location.state?.fromProfileModal)) {
                ProfileStorageManager.clear('individual', personUUID);
            }

            loadProvinces();
            apiVersion.value = `${data.apiName} - ${data.apiVersion}`;
            let address = data.addresses.find((address: Address) => address.is_active);
            let contacts = data.contacts.find((tmp: any) => tmp.type === 'MOBILE' && tmp.is_active);

            if (!address) {
                address = {
                    ...address,
                    id: '',
                    person_id: '',
                    zipcode: '',
                    street: '',
                    street_number: '',
                    complement: '',
                    district: '',
                    city_id: '',
                    street_type: '',
                    is_active: false,
                };
            }

            form.value = {
                ...form.value,
                id: data.id,
                uuid: personId,  // ✅ Usar personId (UUID) ao invés de data.uuid (INTEGER)
                socialId: data.socialId,
                name: data.name,
                birthday: data.birthday,
                maritalStatus: data.maritalStatus ? data.maritalStatus.id : "",
                matrimonialRegimeId: data.matrimonialRegime ? data.matrimonialRegime.id : "",
                mobile: data.mobile,
                phone: data.phone.replace(/\D/g, '').replace(/(\d{2})(\d)/, '($1)$2').replace(/(\d{4})(\d)/, '$1-$2').replace(/(-\d{4})\d+?$/, '$1'),
                email: data.email,
                gender: data.gender,
                isCountryman: data.isCountryman,
                PEP: data.PEP,
                activeAddress: address,
                addresses: data.addresses,
                profiles: data.profiles,
                contacts: data.contacts.map((contact: any) => ({
                    ...contact,
                    isActive: contact.is_active,
                    isDefault: contact.is_default,
                    isNationalNumber: contact.is_national_number
                })),
                idCard: data.idCard ? data.idCard.number : null,
                idCardType: data.idCard ? data.idCard.type : null,
                idCardProvince: data.idCard ? data.idCard.province : null,
                issuingAuthority: data.idCard ? data.idCard.issuingAuthority : null,
                isNationalNumber: contacts?.is_national_number === true ? "true" : "false",
                nationalityId: data.nationalityId,
                professionId: data.professionId,
                professionName: data.professionName || '',
                spouseSocialId: data.spouseSocialId || '',
                spouseName: data.spouseName || '',
                consentingParty: data.consentingParty,
                bankAccounts: data.bankAccounts || [],
            };


            if (address.city_id) {
                const cityId = Number(address.city_id);
                if (!isNaN(cityId)) {
                    getCity(cityId).then(async (city) => {
                        if (city) {
                            const provinceInitials = city["province.initials"] || city?.province?.initials;
                            if (provinceInitials) {
                                form.value = {
                                    ...form.value,
                                    activeAddress: {
                                        ...form.value.activeAddress,
                                        province_initials: provinceInitials,
                                        city_id: String(address.city_id),
                                    }
                                };
                                await fetchCitiesWrapper(provinceInitials);
                            }
                        }
                    });
                }
            }
            addTab(getTabItem("tab-0"));
            addTab(getTabItem("tab-1"));
            addTab(getTabItem("tab-3")); // ✅ SEMPRE mostrar aba Anuentes

            // ✅ SEMPRE adicionar abas ALL (Endereços, Contatos, Documentos)
            profileTabList.forEach(profileTab => {
                if (profileTab.name === 'ALL') {
                    addTab(getTabItem(profileTab.id));
                }
            });

            //Carregar tabs cadastradas no banco de dados
            if (data.profiles) {
                data.profiles.forEach((profile: any) => {
                    profileTabList.forEach(profileTab => {
                        if (profileTab.name.includes(profile.type)) {
                            const tabItem = profileTab;
                            addTab(getTabItem(tabItem.id));
                        }
                    });
                });
            }
            //carregar novos tabs adicionados por um novo perfil
            if (profilesArray) {
                profilesArray.value.forEach((profile: any) => {
                    profileTabList.forEach(profileTab => {
                        if (profileTab.name.includes(profile)) {
                            const tabItem = profileTab;
                            addTab(getTabItem(tabItem.id));
                        }
                    });
                });
            }

            // ✅ Usar profiles do localStorage (loadedProfiles) ao invés da query string
            form.value = {
                ...form.value,
                profiles: loadedProfiles.length > 0 ? loadedProfiles : profiles.split(','),
            }

            banks.value = data.banks || [];
            individualUUID.value = data.uuid;
            isEditing.value = readOnly ? false : (profiles === '' ? true : false);
            formatMobilePhone();
            loadProfilesSubscription();
            alertTrigger.value++; // Forçar reavaliação dos campos obrigatórios
            // ✅ CORREÇÃO: loadProfileList apenas se localStorage estiver vazio
            if (loadedProfiles.length === 0) {
                loadProfileList(isEditing.value);
            }
            checkFieldToBlockEditing(isEditing.value, data);
        }
    }, [data]);

    const loadProfileList = (isEditing: boolean) => {
        if (isEditing) {
            const bankProfiles: string[] = [];
            data.profiles.forEach((profile: any) => {
                bankProfiles.push(profile.type);
            });

            const personUUID = getPersonUUID();
            if (!personUUID) return;
            const localStorageProfiles = ProfileStorageManager.load('individual', personUUID);

            // ✅ LÓGICA: localStorage tem prioridade (mais recente que banco)
            // Só usar banco se localStorage estiver vazio
            if (localStorageProfiles.length > 0) {
                profilesArray.value = localStorageProfiles;
                alertTrigger.value++;
            } else if (bankProfiles.length > 0) {
                profilesArray.value = bankProfiles;
                saveProfiles(bankProfiles); // Sincronizar com localStorage
                alertTrigger.value++;
            }
        }
    }

    const checkFieldToBlockEditing = (isEditing: boolean, data: any) => {
        if (isEditing) {
            form.value.blockEdit = {
                blockIdCard: data.idCard.number !== "",
                blockBirthday: data.birthday !== "",
                blockNationality: data.nationalityId !== null,
            };
        }

    }

    const [currentTab, setCurrentTab] = useState(tabIndex);

    const setTabIndex = (newTabIndex: number) => {
        setCurrentTab(newTabIndex);
    };

    const profileSubscriptionContains = (values: DataRow[], sign_as: string) => {
        var found = false;
        values.forEach((item: DataRow) => {
            if (item.label === sign_as) {
                found = true;;
            }
        });
        return found;
    };

    const loadSpouseName = async (socialId: string) => {
        const token = localStorage.getItem('authToken');
        isLoadingForm.value = true;

        try {
            const response = await fetch(`${BASE_URL}/${socialId}`, {
                method: 'GET',
                headers: {
                    'x-api-key': API_KEY,
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                }
            });

            let spouseName = "";
            if (!response.ok) {
                // Tentar ler mensagem de erro, mas não falhar se não conseguir
                try {
                    await response.json();
                } catch (jsonError) {
                }
                // REMOVIDO: toast.error("Cônjuge não encontrado...");
                spouseName = 'Ainda não cadastrado';
            } else {
                const data = await response.json();
                spouseName = data.name || 'Nome não disponível';
            }

            form.value = {
                ...form.value,
                spouseName: spouseName,
            };
        } catch (error) {
            console.error('Erro ao buscar cônjuge:', error);
            // REMOVIDO: toast.error("Cônjuge não encontrado...");
            form.value = {
                ...form.value,
                spouseName: 'Ainda não cadastrado',
            };
        } finally {
            isLoadingForm.value = false;
        }

        return true;
    }
    const changeForm = (field: string, value: string) => {
        let fieldName = '';
        let attribute = '';
        if (field.includes('.')) {
            fieldName = field.split('.')[0];
            attribute = field.split('.')[1];
        } else {
            fieldName = field;
        }
        if (fieldName === 'activeAddress') {
            form.value = {
                ...form.value,
                activeAddress: {
                    ...form.value.activeAddress,
                    [attribute]: value,
                }
            }
        } else {
            form.value = {
                ...form.value,
                [fieldName]: value,
            }
        }

        // Auto-seleção de regime matrimonial e limpeza de campos
        if (fieldName === 'maritalStatus') {
            let updates: any = {};

            if (value === '2' || value === '6') {
                // Casado ou união estável - auto-selecionar comunhão parcial
                updates.matrimonialRegimeId = '2';
            } else {
                // Outros estados - limpar campos relacionados
                updates.matrimonialRegimeId = '';
                updates.spouseSocialId = '';
                updates.spouseName = '';
            }

            form.value = { ...form.value, ...updates };
        }

        if (fieldName === 'spouseSocialId') {
            let spouseSocialId = form.value.spouseSocialId.replace(/\D/g, '');
            if (spouseSocialId.length === 11) {
                loadSpouseName(spouseSocialId);
            }
            if (spouseSocialId.length < 11) {
                form.value = {
                    ...form.value,
                    spouseName: 'Ainda não cadastrado',
                };
            }

        }
        if (fieldName === 'consentingParty') {
            form.value = {
                ...form.value,
                selectedConsentingParty: value,
            }
        }
        removeErrorField(errorFields.value, fieldName);
    };

    // Ref para controlar logs excessivos
    const lastProfilesRef = { current: '' };

    const generateProfileList = computed(() => {
        // 🚨 FIX: Evitar re-renderização se profiles estão vazios
        if (!profilesArray.value || profilesArray.value.length === 0) {
            return <div>Nenhum perfil associado</div>;
        }

        // 🚨 FIX: Evitar log excessivo - só logar quando há mudança real
        const profilesStr = JSON.stringify(profilesArray.value);
        if (lastProfilesRef.current !== profilesStr) {
            lastProfilesRef.current = profilesStr;
        }

        return (
            <div className="grid grid-cols-1 gap-8">
                <div className="flex space-x-2 col-span-1">
                    {
                        profilesArray.value
                            .filter((profile: string) => profilesMap[profile])
                            .map((profile: string, idx: number) => {
                                const isNew = !dbProfiles.includes(profile);
                                return (
                                    <div className="flex items-center" key={profile + idx}>
                                        <span className={`
                                            px-3 py-1.5 rounded-lg flex items-center gap-2 
                                            backdrop-blur-sm transition-all duration-200
                                            ${isNew
                                                ? 'bg-gradient-to-r from-blue-50/80 to-indigo-50/80 text-blue-700 border border-blue-200/50 shadow-sm hover:shadow-md'
                                                : 'bg-white/60 text-gray-700 border border-gray-200/50 shadow-sm'
                                            }
                                        `}>
                                            {isNew ? (
                                                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                                </svg>
                                            ) : (
                                                <svg className="w-3.5 h-3.5 opacity-50" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                </svg>
                                            )}
                                            <span className="font-medium text-sm">{profilesMap[profile]}</span>
                                        </span>
                                    </div>
                                );
                            })
                    }
                </div>
            </div>
        );
    });

    // Guard clause - renderizar erro se personUUID não existir
    if (!personUUID) {
        console.warn('PersonUUID not found');
        return <div>Error: Person ID not found</div>;
    }

    return (
        <PageForm
            pageTitle={`Alterar pessoa #${form.value.id}`}
            apiVersion={apiVersion.value}
            isLoading={isLoading || isSendingForm.value || isLoadingForm.value}
            breadcrumb={[
                {
                    label: "Listagem de pessoas físicas",
                    url: (() => {
                        const listState = storage.getIndividualListState();
                        const filters = storage.getIndividualFilters();
                        const { page, pageSize, field, order } = listState;
                        const { txtNameSocialId, txtEmail, txtMobile } = filters;
                        return `/individual/individual-list/${page}/${pageSize}/${field}/${order}/p=${txtNameSocialId};r=${txtMobile};s=${txtEmail};`;
                    })()
                },
                { label: `Editar pessoa física #${form.value.id}`, url: "#" },
            ]}
            controlSlot={(
                <div className="flex space-x-4 justify-end m-2">
                    <button
                        id="btn-cancel-individual"
                        className='text-black bg-slate-200 px-4 py-2 hover:bg-slate-400 rounded'
                        onClick={readOnly ? closeForm : cancelForm}
                        title={readOnly ? "Fechar" : "Cancelar registro"}
                        disabled={isLoading || isSendingForm.value}
                    >
                        {readOnly ? 'Fechar' : 'Cancelar'}
                    </button>

                    {!readOnly &&
                        <button
                            id="btn-save-individual"
                            className="bg-cyan-500 text-white px-4 py-2 rounded hover:bg-cyan-800 disabled:bg-gray-300 disabled:text-gray-500"
                            onClick={submitForm}
                            title="Atualizar registro"
                            disabled={updateButtonDisable.value}

                        >
                            Atualizar
                        </button>
                    }
                </div>
            )}
        >
            <div>
                <div className="grid grid-cols-1 gap-8">
                    <div className="col-span-1">
                        <h2 className="text-lg font-semibold text-gray-800">
                            {form.value.name}
                        </h2>
                    </div>
                </div>
                <div className="grid grid-cols-1 gap-8">
                    <div className="flex space-x-1 col-span-1">
                        {generateProfileList.value}
                    </div>

                </div>
            </div>

            <Fragment>
                <Tab tabItems={tabItems.value} tabIndex={currentTab} setTabIndex={setTabIndex}></Tab>

                <div id="tab-content" className="overflow-y-auto">

                    {currentTab === 0 &&
                        <IndividualFormBasicData
                            key="tab1"
                            form={form.value}
                            errorFields={errorFields.value}
                            changeForm={changeForm}
                            provinces={provinces.value}
                            cities={cities.value}
                            changeProvince={fetchCitiesWrapper}
                            editForm={isEditing.value}
                            readOnly={readOnly}
                        />
                    }

                    {currentTab === 1 &&
                        <IndividualFormAdditionalData
                            key="tab2"
                            form={form.value}
                            profiles={profilesArray.value}
                            errorFields={errorFields.value}
                            changeForm={changeForm}
                            provinces={provinces.value}
                            cities={cities.value}
                            genders={genders.value}
                            changeProvince={fetchCitiesWrapper}
                            editForm={isEditing.value}
                            readOnly={readOnly}
                            onProfessionChange={(profId, profName) => { form.value = { ...form.value, professionId: profId, professionName: profName }; }}
                        />
                    }

                    {currentTab === 2 &&
                        <IndividualFormSubscription
                            key="tab3"
                            form={form.value}
                            subscriptionTypes={subscriptionTypes.value}
                            profilesSubscription={profilesSubscription.value}
                            errorFields={errorFields.value}
                            changeForm={changeForm}
                            editForm={isEditing.value}
                            readOnly={readOnly}
                        />
                    }

                    {currentTab === 3 &&
                        <IndividualFormConsentingParty
                            key="tab4"
                            personId={personId || ''}
                            personType="individual"
                            form={form.value}
                            errorFields={errorFields.value}
                            changeForm={changeForm}
                            editForm={isEditing.value}
                            readOnly={readOnly}
                        />
                    }

                    {currentTab === 4 &&
                        <PersonFormAddresses
                            personId={personId || ''}
                            personType="individual"
                            readOnly={readOnly}
                        />
                    }

                    {currentTab === 5 &&
                        <PersonFormContacts
                            personId={personId || ''}
                            personType="individual"
                            readOnly={readOnly}
                        />
                    }

                    {currentTab === 6 &&
                        <PersonFormDocuments
                            personId={personId || ''}
                            personType="individual"
                            readOnly={readOnly}
                        />
                    }

                    {currentTab === 7 &&
                        <PersonFormBankAccount
                            personId={personId || ''}
                            personType="individual"
                            provinces={provinces.value}
                            banks={banks.value}
                            bankAccounts={form.value.bankAccounts}
                            onBankAccountChange={handleBankAccountChange}
                            readOnly={readOnly}
                        />
                    }

                </div>

                {missingFields.length > 0 && (
                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm font-medium text-yellow-800 mb-2">⚠️ Campos obrigatórios não preenchidos:</p>
                        <ul className="list-disc list-inside text-sm text-yellow-700 space-y-1">
                            {missingFields.map((field, idx) => {
                                const addressFields = ['CEP', 'Tipo de logradouro', 'Logradouro', 'Número', 'Bairro', 'Estado', 'Cidade'];
                                const tab = addressFields.includes(field) ? 'Dados básicos' : 'Dados Complementares';
                                return <li key={idx}>{field} <span className="text-xs italic">(aba: {tab})</span></li>;
                            })}
                        </ul>
                        <p className="text-xs text-yellow-600 mt-2">Preencha estes campos para habilitar o botão Atualizar.</p>
                    </div>
                )}

            </Fragment>



        </PageForm >
    );
};


export default IndividualPageEdit;// Force deploy Seg 22 Dez 2025 08:37:46 -03
